import { Modal } from '@zambezi/sdk/modal';
import React, { useEffect, useRef, useState } from 'react';
import { IIdleTimer, useIdleTimer } from 'react-idle-timer';
import { clearInterval, setInterval } from 'worker-timers';
import { convertMillisecondsToMinutesAndSeconds } from '../../utils/date';
import './InactivityPrompt.scss';

interface InactivityPromptProps {
  sessionTimeoutMinutes?: number;
  sessionTimeoutWarningMinutes?: number;
  timeoutMinutes?: number;
  promptMinutes?: number;
  onTimeout?: () => void;
  onConfirm?: () => void;
  showTimer?: boolean;
}
const InactivityPrompt: React.FC<InactivityPromptProps> = ({
  sessionTimeoutMinutes = 60,
  sessionTimeoutWarningMinutes = 50,
  timeoutMinutes = 15,
  promptMinutes = 1,
  onTimeout,
  onConfirm,
  showTimer = false
}: InactivityPromptProps) => {
  const timeElapsedSinceLastSessionWasActive = useRef<number>(0);
  const timer = useRef<number>(null);

  const [totalElapsed, setTotalElapsed] = useState(
    Number(sessionStorage.getItem('totalElapsedTime') || 0)
  );
  const [remaining, setRemaining] = useState(0);
  const [isPromptActive, setIsPromptActive] = useState(false);

  const onPrompt = () => {
    setIsPromptActive(true);
  };

  const onIdle = (_: Event, idleTimer: IIdleTimer) => {
    idleTimer.pause();
    setIsPromptActive(false);
    onTimeout?.();
  };

  const { getRemainingTime, reset, start, getTotalElapsedTime } = useIdleTimer({
    timeout: 1000 * 60 * timeoutMinutes,
    promptBeforeIdle: 1000 * 60 * promptMinutes,
    throttle: 500,
    onPrompt,
    onIdle
  });

  const onSessionContinue = () => {
    reset();
    setIsPromptActive(false);
    onConfirm?.();
  };

  const setTotalElapsedTimeInSessionStorage = () => {
    sessionStorage.setItem('lastSessionTimestamp', `${Date.now()}`);
    sessionStorage.setItem('totalElapsedTime', `${totalElapsed}`);
  };

  const sessionTimeoutInSeconds = 60 * sessionTimeoutMinutes;
  const sessionTimeoutWarningInSeconds = 60 * sessionTimeoutWarningMinutes;
  const sessionTimeoutInMs = 1000 * 60 * sessionTimeoutMinutes;
  const totalElapsedTimeInMilliseconds = (1000 * 60 * totalElapsed) / 60;

  useEffect(() => {
    const lastSessionTimestamp = Number(sessionStorage.getItem('lastSessionTimestamp') || 0);
    const diffBetweenNowAndLastActiveSession = lastSessionTimestamp
      ? Math.ceil((Date.now() - lastSessionTimestamp) / 1000)
      : 0;

    timeElapsedSinceLastSessionWasActive.current = diffBetweenNowAndLastActiveSession;
  }, []);

  useEffect(() => {
    timer.current = setInterval(() => {
      const newTotalElapsedTime = Math.ceil(
        Number(sessionStorage.getItem('totalElapsedTime')) + getTotalElapsedTime() / 1000
      );
      setRemaining(Math.ceil(getRemainingTime() / 1000));
      setTotalElapsed(newTotalElapsedTime + timeElapsedSinceLastSessionWasActive.current);
    }, 500);

    return () => {
      clearInterval(timer.current);
      timer.current = null;
    };
  });

  useEffect(() => {
    window.addEventListener('beforeunload', setTotalElapsedTimeInSessionStorage);
    return () => window.removeEventListener('beforeunload', setTotalElapsedTimeInSessionStorage);
  });

  useEffect(() => {
    if (totalElapsed >= sessionTimeoutInSeconds) {
      onTimeout?.();
    }
  }, [onTimeout, sessionTimeoutInSeconds, totalElapsed]);

  useEffect(() => {
    start();
  }, [start]);

  return (
    <>
      <div aria-live="assertive">
        {totalElapsed >= sessionTimeoutWarningInSeconds && (
          <div className="mss-session-timeout-banner">
            Your application will time out in{' '}
            {convertMillisecondsToMinutesAndSeconds(
              Math.ceil(sessionTimeoutInMs - totalElapsedTimeInMilliseconds)
            )}{' '}
            minutes to keep you safe
          </div>
        )}
      </div>
      {showTimer && (
        <div className="mss-session-timer">
          Inactivity timeout: {convertMillisecondsToMinutesAndSeconds(remaining * 1000)}
          <br />
          Session timeout:{' '}
          {convertMillisecondsToMinutesAndSeconds(
            Math.ceil(sessionTimeoutInMs - totalElapsedTimeInMilliseconds)
          )}
          <br />
          Total Elapsed: {convertMillisecondsToMinutesAndSeconds(totalElapsed * 1000)}
        </div>
      )}
      {isPromptActive && (
        <Modal
          title="Your session is about to expire"
          confirm="Continue your session"
          onConfirm={onSessionContinue}
          isClosable={false}
          open
        >
          Do you want to continue your session? If you do nothing, we'll log you out in {remaining}{' '}
          seconds to keep your account safe.
        </Modal>
      )}
    </>
  );
};

export default InactivityPrompt;
